/* eslint-disable dot-notation */

import { forwardRef } from 'react';
import {
  BoxProps,
  FormControl,
  FormHelperText,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputProps,
  InputRightElement,
} from '@chakra-ui/react';
import { FieldError, FieldErrors, UseFormRegister } from 'react-hook-form';

import { FormIconError } from 'components/atoms/formIconError';
import { IconType } from 'lib/@types';
import { Error } from 'components/atoms/form/error';
import { FieldTitle } from 'components/atoms/form/fieldTitle';
import getErrorMessage from 'lib/utils/getErrorMessage';
import { VariantsAvailable as FieldTitleVariantsAvailable } from 'components/atoms/form/fieldTitle/variants';

import { VariantsAvailable, variants } from './variants';

export interface FieldProps extends Omit<InputProps, 'h' | 'w'> {
  variant?: VariantsAvailable;
  label?: string;
  labelVariant?: FieldTitleVariantsAvailable;
  starColor?: BoxProps['color'];
  name?: string;
  errors?: FieldErrors;
  error?: FieldError;
  leftIcon?: IconType;
  rightIcon?: IconType;
  iconColor?: BoxProps['color'];
  showErrorIcon?: boolean;
  onRightIconClick?: () => void;
  onLeftIconClick?: () => void;
  register?: UseFormRegister<Record<string, unknown>>;
  helpText?: string;
  borderColor?: string;
  helpTextMiddle?: string;
}

const Field = forwardRef<HTMLInputElement, FieldProps>(
  (
    {
      borderColor,
      variant = 'outline',
      onRightIconClick,
      onLeftIconClick,
      height = '40px',
      width = '100%',
      type = 'text',
      labelVariant,
      placeholder,
      isRequired,
      starColor,
      rightIcon,
      iconColor = 'text.100',
      leftIcon,
      register,
      errors, // TODO: Refactor: Remove errors prop, it is not necessary to send all errors in the form to the field, just send the specific message
      error,
      label,
      name,
      showErrorIcon = false,
      helpText,
      helpTextMiddle,
      ...props
    },
    ref
  ) => {
    const errorMessage = errors
      ? getErrorMessage(name, errors)
      : error?.message;

    const registerProps = register && name ? register(name) : {};

    return (
      <FormControl
        {...props}
        {...registerProps}
        position='relative'
        width={width}
      >
        <FieldTitle
          isRequired={isRequired}
          label={label}
          variant={labelVariant}
          starColor={starColor}
        />

        {helpTextMiddle && (
          <FormHelperText
            fontStyle='italic'
            color='text.200'
            fontSize='0.9rem'
            mt={0}
            pb={4}
          >
            {helpTextMiddle}
          </FormHelperText>
        )}

        <InputGroup flexDirection='column'>
          {leftIcon && (
            <InputLeftElement
              cursor={onLeftIconClick ? 'pointer' : 'default'}
              onClick={onLeftIconClick}
              height={height}
            >
              <Icon as={leftIcon} color='text.100' w={5} h={5} mx={4} />
            </InputLeftElement>
          )}

          <Input
            data-testid='input'
            aria-label={label}
            placeholder={placeholder}
            height={height}
            width={width}
            type={type}
            name={name}
            ref={ref}
            paddingRight='12px'
            borderRadius={4}
            fontWeight='400'
            fontSize='14px'
            py={2}
            {...variants[variant]}
            borderColor={
              errorMessage
                ? 'errors.200'
                : borderColor ?? variants[variant].borderColor
            }
            _focus={{
              borderColor: errorMessage
                ? 'errors.200'
                : variants[variant]['_focus']?.borderColor,
              bgColor: variants[variant]['_focus']?.bgColor,
              boxShadow: 'none',
            }}
            _hover={{
              borderColor: errorMessage
                ? 'errors.200'
                : variants[variant]['_hover']?.borderColor,
            }}
          />

          {rightIcon && (
            <InputRightElement
              cursor={onRightIconClick ? 'pointer' : 'default'}
              onClick={onRightIconClick}
              height={height}
            >
              <Icon as={rightIcon} color={iconColor} w={5} h={5} mx={4} />
            </InputRightElement>
          )}

          {errorMessage && <Error width={width} innerHTML={errorMessage} />}
        </InputGroup>

        {helpText && (
          <FormHelperText fontStyle='italic' color='text.200' fontSize='0.9rem'>
            {helpText}
          </FormHelperText>
        )}
        {errorMessage && showErrorIcon && !helpTextMiddle && <FormIconError />}
        {errorMessage && showErrorIcon && helpTextMiddle && (
          <FormIconError top='107px' />
        )}
      </FormControl>
    );
  }
);

Field.displayName = 'Field';

export default Field;
